import * as React from "react";
import get from "lodash/get";

import "./LedgerTotal.css";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { getLedgerHead, getRewardRequestStatus } from "../../reducers/methods";
import { RequestStatus } from "../../types/requestStatus";
import { currencySymbols } from "../../utils/currencies";
import { formatUserFacingNumber } from "../../utils/number";

export interface LedgerTotalProps extends ComponentBaseProps {
  action: string;
  center: string;
  classNames: string;
  label: string;
  ledgerHead: ReturnType<typeof getLedgerHead>;
  onClick: () => void;
  requestStatus: ReturnType<typeof getRewardRequestStatus>;
  currency?: string;
  currencyLabel?: string;
  style: { [styleProp: string]: string };
  /**
   * Optional style overrides applied only to the amount span (the number, e.g. "50").
   * When absent, the amount inherits typography from the container `style`.
   */
  amountStyle?: { [styleProp: string]: string };
  /**
   * Optional style overrides applied only to the currency-label span (e.g. "AAA").
   * When absent, the label inherits typography from the container `style`.
   *
   * Only applies when a retailer-configured `currencyLabel` is present. In
   * the currency-symbol fallback branch (e.g. "$50") there is no standalone
   * label element to style — symbol and number are rendered together in the
   * same amount span to keep them visually flush — so `currencyStyle` is
   * intentionally ignored there. `amountStyle` is honored in BOTH branches.
   */
  currencyStyle?: { [styleProp: string]: string };
  /**
   * Controls which parts of the ledger balance are rendered:
   *  - "both"     (default): amount and currency label together (legacy behavior).
   *  - "amount":   only the amount (e.g. "50"). Currency label / symbol omitted.
   *  - "currency": only the currency label (e.g. "AAA"), or currency symbol when
   *                no `currencyLabel` is configured (e.g. "$"). Amount omitted.
   *
   * The widget builder exposes this through separate "Ledger Amount" and
   * "Ledger Currency" palette entries, so a designer can drop each onto the
   * canvas and position / style them independently. The single-element
   * "Ledger Total" / "Points Balance" palette entries keep `"both"` so
   * existing widgets render unchanged.
   */
  displayMode?: "both" | "amount" | "currency";
}

function getRawBalance(
  ledgerHead: ReturnType<typeof getLedgerHead>,
  isEditing: boolean
): number {
  return Number(get(ledgerHead, "balance", isEditing ? 50 : 0));
}

// tslint:disable-next-line:no-any
export class LedgerTotal extends ComponentBase<LedgerTotalProps> {
  render() {
    const {
      classNames,
      currencyLabel,
      data,
      label = "ledger_total",
      isEditing,
      ledgerHead,
      requestStatus,
      currency = "loyalty",
      amountStyle,
      currencyStyle,
      displayMode = "both",
    } = this.props;
    const isCustomerAuthenticated = get(data, "customer.authenticated", false);
    const getLedgerRequestStatus = get(
      requestStatus,
      "getLedger"
    ) as RequestStatus;
    if (!isCustomerAuthenticated && !isEditing) {
      return null;
    }

    const balance = getRawBalance(ledgerHead, isEditing);
    const formattedAmount = formatUserFacingNumber(balance);
    const currencySymbol =
      currency === "loyalty" ? "" : get(currencySymbols, currency, currency);

    // The widget builder treats this entire component's outer <div> as one
    // draggable unit, so to support independent positioning (QA #12) the
    // palette registers two additional entries ("Ledger Amount",
    // "Ledger Currency") that preset `displayMode` below. Each entry lands
    // on the canvas as its own draggable/stylable element; designers can
    // place a plain Text component between them (e.g. " of ") to build
    // phrases like "50 of AAA" from three free-floating elements.
    //
    //   displayMode: "both"     → legacy combined render (default)
    //   displayMode: "amount"   → only the amount span
    //   displayMode: "currency" → only the currency label / symbol span
    //
    // The "amount" branch still uses the `LedgerTotal-amount` className so
    // any retailer CSS targeting it keeps working.
    const showAmount = displayMode !== "currency";
    const showCurrency = displayMode !== "amount";

    const amountNode = showAmount && (
      <span className="LedgerTotal-amount" style={amountStyle}>
        {/* In "amount" mode the span renders just the number. In "both" mode
         * without a currencyLabel we keep the existing "$50"-style tight
         * prefix so we don't regress that visual. When currencyLabel IS set
         * the symbol is redundant with the label, so it's omitted. */}
        {!currencyLabel && displayMode === "both"
          ? `${currencySymbol}${formattedAmount}`
          : formattedAmount}
      </span>
    );

    const currencyNode = showCurrency &&
      // Nothing meaningful to render for "loyalty" with no label — match
      // legacy behavior which rendered no symbol in that case.
      (currencyLabel || currencySymbol) && (
        <span className="LedgerTotal-currency" style={currencyStyle}>
          {currencyLabel || currencySymbol}
        </span>
      );

    // Legacy "both" mode keeps a literal space between amount and label for
    // backward compatibility with retailer stylesheets that don't add their
    // own inter-span spacing. Independent modes never insert whitespace —
    // positioning is the designer's responsibility.
    const balanceContent =
      displayMode === "both" ? (
        <>
          {amountNode}
          {amountNode && currencyNode ? " " : null}
          {currencyNode}
        </>
      ) : (
        amountNode || currencyNode
      );

    const ariaLabel =
      displayMode === "amount"
        ? `${label} ${formattedAmount}`
        : displayMode === "currency"
        ? `${label} ${currencyLabel || currencySymbol || ""}`.trim()
        : currencyLabel
        ? `${label} ${formattedAmount} ${currencyLabel}`
        : label;

    return (
      <div
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onAnimationEnd={this.resetAnimation}
        style={this.props.style}
        className={`LedgerTotal-container  ${classNames || ""} ${
          this.state.animationClass
        }`}
        aria-label={ariaLabel}
        ref={(component) => (this.component = component)}
      >
        {getLedgerRequestStatus === "REQUESTING" && <span>...</span>}
        {(getLedgerRequestStatus === "SUCCESS" || isEditing) && balanceContent}
      </div>
    );
  }
}
